import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { roleService } from '../../services/auth/RoleService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'student' | 'teacher' | 'admin';
  requiredPermission?: string;
  fallbackPath?: string;
}

/**
 * Protected Route Component
 * Bảo vệ route dựa trên role và permission
 * 
 * Usage:
 * <ProtectedRoute requiredRole="admin">
 *   <AdminDashboard />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallbackPath = '/'
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // ✅ Debug logs
  console.log('🔐 ProtectedRoute:', {
    path: location.pathname,
    isLoading,
    isAuthenticated,
    userRole: user?.role,
    userEmail: user?.email,
    requiredRole,
    requiredPermission,
  });

  // Show loading state
  if (isLoading) {
    console.log('⏳ ProtectedRoute: Loading...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Đang xác thực...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated || !user) {
    console.warn('❌ ProtectedRoute: Not authenticated, redirecting to login');
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check role requirement
  if (requiredRole) {
    const hasRequiredRole = user.role === requiredRole ||
      (requiredRole === 'teacher' && user.role === 'admin') ||
      (requiredRole === 'student' && (user.role === 'admin' || user.role === 'teacher'));

    console.log('🔍 Role check:', { userRole: user.role, requiredRole, hasRequiredRole });

    if (!hasRequiredRole) {
      console.warn(`❌ Access denied: User has role "${user.role}" but requires "${requiredRole}"`);
      return (
        <Navigate
          to={fallbackPath}
          state={{
            error: 'Bạn không có quyền truy cập trang này',
            from: location
          }}
          replace
        />
      );
    }
  }

  // Check permission requirement
  if (requiredPermission) {
    const userPermissions: string[] = Array.isArray(user.permissions)
      ? user.permissions
      : (user.permissions as any)?.capabilities || [];

    const hasPermission = roleService.hasPermission(userPermissions, requiredPermission);

    if (!hasPermission) {
      console.warn(`Access denied: User lacks permission "${requiredPermission}"`);
      return (
        <Navigate
          to={fallbackPath}
          state={{
            error: 'Bạn không có quyền truy cập chức năng này',
            from: location
          }}
          replace
        />
      );
    }
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default ProtectedRoute;

